import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { msToHoursInTime, msToMinutesInTime, msToSecondsInTime } from '@/utils/datetime/msToTimeUnit'
import { msToDays } from '@/utils/datetime/msToUnit'

type Props = {
	date: Date
	/**
	 * Callback to be executed once the countdown is finished
	 */
	onElapsed?: () => void
}

const formatMsToUnits = (countDown: number) => ({
	days: msToDays(countDown),
	hours: msToHoursInTime(countDown),
	minutes: msToMinutesInTime(countDown),
	seconds: msToSecondsInTime(countDown)
})

export const useDateCountdown = ({ date, onElapsed }: Props) => {
	const initialTime = useMemo(() => new Date(date).getTime(), [date])
	const intervalToken = useRef({})

	const [countDown, setCountDown] = useState<number>(initialTime - new Date().getTime())

	/**
	 * Start/resume the countdown
	 */
	const start = useCallback(() => {
		// If the provided date is already elapsed, refrain from starting the interval and execute the onElapsed callback
		if (Date.now() >= initialTime) {
			setCountDown(0)
			onElapsed?.()

			return
		}

		// For immediate counter update
		setCountDown(initialTime - new Date().getTime())

		const interval = window.setInterval(() => {
			const updatedCountDown = Math.max(initialTime - new Date().getTime(), 0)

			setCountDown(updatedCountDown)
		}, 1000)

		intervalToken.current = interval as number
	}, [initialTime, countDown])

	/**
	 * Stop the countdown
	 */
	const stop = useCallback(() => {
		clearInterval(intervalToken.current as number)
	}, [intervalToken])

	useEffect(() => {
		stop()
		start()

		return () => stop()
	}, [initialTime])

	useEffect(() => {
		if (countDown <= 0) {
			stop()
			onElapsed?.()
		}
	}, [countDown])

	return {
		...formatMsToUnits(countDown),
		start,
		stop
	} as const
}
